import React, { useState } from 'react';
import { Card } from '../Card';
import { Logo } from '../Logo';
import { addRecentRoom, getRecentRooms, toRoomId } from '../../utils/rooms';
import { DEFAULT_ROOM_THEME, ROOM_THEME_OPTIONS, RoomTheme } from '../../types/RoomTheme';
import { DEFAULT_ROOM_MODE, ROOM_MODE_OPTIONS, RoomMode } from '../../types/RoomMode';
import './styles.css';

interface Props {
    onJoinRoom: (roomId: string) => Promise<{ ok: boolean; reason?: 'not-found' }>;
    onCreateRoom: (roomId: string, theme: RoomTheme, mode: RoomMode) => Promise<{ roomId: string; created: boolean }>;
}

const RoomSelector: React.FC<Props> = ({ onJoinRoom, onCreateRoom }) => {
    const [mode, setMode] = useState<'create' | 'join'>('create');
    const [name, setName] = useState('');
    const [conflictRoomId, setConflictRoomId] = useState<string | null>(null);
    const [joinError, setJoinError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recentRooms, setRecentRooms] = useState<string[]>(() => getRecentRooms());
    const [selectedTheme, setSelectedTheme] = useState<RoomTheme>(DEFAULT_ROOM_THEME);
    const [selectedMode, setSelectedMode] = useState<RoomMode>(DEFAULT_ROOM_MODE);

    const switchMode = (nextMode: 'create' | 'join') => {
        setMode(nextMode);
        setConflictRoomId(null);
        setJoinError(null);
        setValidationError(null);
    };

    const joinRoom = async (roomId: string) => {
        const cleanId = toRoomId(roomId);
        if (!cleanId) return;
        setJoinError(null);
        setValidationError(null);
        const result = await onJoinRoom(cleanId);
        if (!result.ok) {
            setJoinError('La sala no existe o aún no fue creada por un narrador.');
            return;
        }
        const nextRecents = addRecentRoom(cleanId);
        setRecentRooms(nextRecents);
    };

    const handleCreate = async () => {
        const cleanId = toRoomId(name);
        if (isSubmitting) return;
        if (!cleanId) {
            setValidationError('Escribe un nombre de sala.');
            return;
        }

        setIsSubmitting(true);
        setConflictRoomId(null);
        setValidationError(null);
        try {
            const result = await onCreateRoom(cleanId, selectedTheme, selectedMode);
            if (!result.created) {
                setConflictRoomId(result.roomId);
                return;
            }
            await joinRoom(result.roomId);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Card size="medium">
            <div className="room-selector">
                <div className="room-selector-logo">
                    <Logo />
                </div>
                <h3>Crea una sala o entra para empezar a jugar</h3>

                <fieldset className="room-selector-mode">
                    <label className="room-selector-radio">
                        <input
                            type="radio"
                            name="room-mode"
                            value="create"
                            checked={mode === 'create'}
                            onChange={() => switchMode('create')}
                        />
                        <span>Crear sala</span>
                    </label>
                    <label className="room-selector-radio">
                        <input
                            type="radio"
                            name="room-mode"
                            value="join"
                            checked={mode === 'join'}
                            onChange={() => switchMode('join')}
                        />
                        <span>Unirse a sala</span>
                    </label>
                </fieldset>

                <p className="room-selector-description">
                    {mode === 'create'
                        ? 'Crea una sala como narrador. Tus jugadores se podrán unir mediante un enlace.'
                        : 'Únete a una sala ya creada introduciendo su nombre.'}
                </p>

                <div className="room-selector-actions">
                    <input
                        placeholder="Nombre de la sala"
                        value={name}
                        onChange={e => {
                            setName(e.target.value);
                            if (validationError) setValidationError(null);
                        }}
                    />
                    {mode === 'create' ? (
                        <button
                            className="button"
                            onClick={() => { void handleCreate(); }}
                        >
                            Crear
                        </button>
                    ) : (
                        <button
                            className="button"
                            onClick={() => {
                                if (!toRoomId(name)) {
                                    setValidationError('Escribe un nombre de sala.');
                                    return;
                                }
                                void joinRoom(name);
                            }}
                        >
                            Unirse
                        </button>
                    )}
                </div>
                {mode === 'create' ? (
                    <>
                        <div className="room-selector-theme">
                            <label htmlFor="room-theme">Tema de la sala</label>
                            <select
                                id="room-theme"
                                value={selectedTheme}
                                onChange={event => setSelectedTheme(event.target.value as RoomTheme)}
                            >
                                {ROOM_THEME_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="room-selector-theme">
                            <label htmlFor="room-game-mode">Modo de juego</label>
                            <select
                                id="room-game-mode"
                                value={selectedMode}
                                onChange={event => setSelectedMode(event.target.value as RoomMode)}
                            >
                                {ROOM_MODE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                ) : null}
                {validationError && <div className="room-selector-error">{validationError}</div>}
                {joinError && <div className="room-selector-error">{joinError}</div>}

                {conflictRoomId && (
                    <div className="room-selector-warning">
                        <p>La sala ya existe.</p>
                        <button className="button" onClick={() => { void joinRoom(conflictRoomId); }}>
                            Unirse
                        </button>
                    </div>
                )}

                <div className="room-selector-recents">
                    <strong>Salas recientes:</strong>
                    {recentRooms.length === 0 ? (
                        <p className="room-selector-empty">Sin salas recientes.</p>
                    ) : (
                        <div className="room-selector-recent-list">
                            {recentRooms.map(roomId => (
                                <button
                                    key={roomId}
                                    className="button-link room-recent-item"
                                    type="button"
                                    onClick={() => { void joinRoom(roomId); }}
                                >
                                    {roomId}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

export default RoomSelector;
